import React from 'react';
import './AnalogClock.css';

/**
 * Dynamic SVG Analog Clock Component
 * @param {number} hour - Hour of the day (1-12 or 0-23)
 * @param {number} minute - Minute of the hour (0-59)
 * @param {number} size - Clock size in pixels (default 240)
 * @param {string} className - Optional container styling override
 */
const AnalogClock = ({ hour = 12, minute = 0, size = 240, className = '' }) => {
  // Normalize hour to 12-hour format
  const normalizedHour = hour % 12;

  // 1. Minute Hand Angle: 360° / 60 minutes = 6° per minute
  const minuteAngle = minute * 6;

  // 2. Hour Hand Angle: 360° / 12 hours = 30° per hour 
  // Plus smooth movement per minute: 30° / 60 = 0.5° per minute
  const hourAngle = normalizedHour * 30 + minute * 0.5;

  const center = size / 2;
  const radius = center - 12;

  return (
    <div className={`analog-clock-container ${className}`} style={{ width: size, height: size }}>
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        className="analog-clock-svg"
      >
        {/* Outer Clock Dial & Shadow */}
        <circle
          cx={center}
          cy={center}
          r={radius}
          className="clock-face"
        />

        {/* Outer Ring Border */}
        <circle
          cx={center}
          cy={center}
          r={radius}
          className="clock-border"
        />

        {/* 60 Minute / Second Tick Marks */}
        {[...Array(60)].map((_, i) => {
          const isMajor = i % 5 === 0;
          const tickAngle = (i * 6 * Math.PI) / 180;
          const innerR = isMajor ? radius - 14 : radius - 8;
          const outerR = radius - 4;

          const x1 = center + innerR * Math.sin(tickAngle);
          const y1 = center - innerR * Math.cos(tickAngle);
          const x2 = center + outerR * Math.sin(tickAngle);
          const y2 = center - outerR * Math.cos(tickAngle);

          return (
            <line
              key={i}
              x1={x1}
              y1={y1}
              x2={x2}
              y2={y2}
              className={isMajor ? 'tick-major' : 'tick-minor'}
            />
          );
        })}

        {/* 12 Hour Numbers */}
        {[...Array(12)].map((_, i) => {
          const number = i + 1;
          const numAngle = (number * 30 * Math.PI) / 180;
          const numRadius = radius - 28;
          const x = center + numRadius * Math.sin(numAngle);
          const y = center - numRadius * Math.cos(numAngle);

          return (
            <text
              key={number}
              x={x}
              y={y}
              className="clock-number"
              fontSize={size * 0.085}
            >
              {number}
            </text>
          );
        })}

        {/* Hour Hand (Short & Thick) */}
        <line
          x1={center}
          y1={center}
          x2={center}
          y2={center - radius * 0.52}
          className="hand-hour"
          transform={`rotate(${hourAngle} ${center} ${center})`}
        />

        {/* Minute Hand (Longer & Thinner) */}
        <line
          x1={center}
          y1={center}
          x2={center}
          y2={center - radius * 0.76}
          className="hand-minute"
          transform={`rotate(${minuteAngle} ${center} ${center})`}
        />

        {/* Center Pivot Pin */}
        <circle cx={center} cy={center} r={size * 0.035} className="clock-pin-outer" />
        <circle cx={center} cy={center} r={size * 0.018} className="clock-pin-inner" />
      </svg>
    </div>
  );
};

export default AnalogClock;